import { Button, Spinner } from '@heroui/react'
import { type BulkInviteRequest, isBulkInviteActive } from '@qse/api'
import { CheckCircle2, Info, XCircle } from 'lucide-react'

import CountStat from './count-stat'
import EmailsNotSentNote from './emails-not-sent-note'
import { getBulkInviteProgress, getBulkInviteResolvedRows } from './helpers'
import RowErrorsTable from './row-errors-table'
import StatusChip from './status-chip'

interface TrackingViewProps {
  request?: BulkInviteRequest
  isLoading: boolean
  /** Total rows from the 202 response, shown before the first status fetch lands. */
  queuedTotal: number | null
  onClose: () => void
}

/**
 * PARTIAL now covers two very different situations — rows we chose not to send, and
 * rows we tried to send and couldn't. Naming which one happened is the difference
 * between an admin shrugging and an admin investigating.
 */
const summarizeUnsent = (skippedCount: number, failureCount: number): string => {
  const parts: string[] = []

  if (skippedCount > 0) parts.push(`${skippedCount.toLocaleString()} skipped`)
  if (failureCount > 0) parts.push(`${failureCount.toLocaleString()} failed`)

  return parts.length ? `${parts.join(' and ')}.` : ''
}

export default function TrackingView({
  request,
  isLoading,
  queuedTotal,
  onClose,
}: TrackingViewProps) {
  // Before the first status response arrives, show a queued spinner.
  if (!request) {
    return (
      <div className="flex flex-col items-center gap-4 py-10 text-center">
        <Spinner size="lg" />
        <div>
          <p className="font-medium">
            Queued{queuedTotal ? ` — ${queuedTotal.toLocaleString()} users` : ''}
          </p>
          <p className="text-sm text-gray-500 dark:text-[#AFAFAF]">
            {isLoading ? 'Fetching status…' : 'Waiting for the worker to start.'}
          </p>
        </div>
        <Button radius="sm" variant="flat" onPress={onClose}>
          Run in background
        </Button>
      </div>
    )
  }

  const active = isBulkInviteActive(request.status)
  const processed = getBulkInviteResolvedRows(request)
  const progress = getBulkInviteProgress(request)

  return (
    <div className="flex flex-col gap-5 py-5 w-full">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {request.status === 'SUCCEEDED' && (
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
          )}
          {request.status === 'FAILED' && <XCircle className="w-5 h-5 text-red-500" />}
          <span className="font-medium">
            {active ? 'Processing invitations' : 'Bulk invite complete'}
          </span>
        </div>
        <StatusChip status={request.status} />
      </div>

      {active ? (
        <div className="flex flex-col gap-2">
          {request.status === 'QUEUED' ? (
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-[#AFAFAF]">
              <Spinner size="sm" />
              <span>Queued — waiting for the worker.</span>
            </div>
          ) : (
            <>
              <div className="w-full bg-gray-200 dark:bg-[#2A2A2A] rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-[#AFAFAF]">
                {processed.toLocaleString()} of {request.totalRows.toLocaleString()} processed (
                {progress}%)
              </p>
            </>
          )}
        </div>
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <CountStat label="Invited" value={request.successCount} />
        <CountStat label="Skipped" value={request.skippedCount} />
        <CountStat label="Failed" value={request.failureCount} />
        <CountStat label="Total" value={request.totalRows} />
      </div>

      <EmailsNotSentNote count={request.emailsNotSent} status={request.status} />

      {request.status === 'PARTIAL' && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
          <Info className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 dark:text-amber-100">
            {request.successCount.toLocaleString()}{' '}
            {request.successCount === 1 ? 'user was' : 'users were'} invited.{' '}
            {summarizeUnsent(request.skippedCount, request.failureCount)} Open Details in History to
            see every row.
          </p>
        </div>
      )}

      {request.errors && request.errors.length > 0 && <RowErrorsTable errors={request.errors} />}

      <div className="flex justify-end gap-3 w-full">
        <Button color="primary" radius="sm" onPress={onClose}>
          {active ? 'Run in background' : 'Done'}
        </Button>
      </div>
    </div>
  )
}
